import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiSuccess } from '../models/api-response.model';

export interface MiVendedor {
  codigo: string;
  nombre: string;
  whatsapp: string | null;
  // Cuándo llegó por su link (vence a los 60 días)
  en: number;
}

const CLAVE = 'vitrina.mi-vendedor.v1';
const VIGENCIA_MS = 60 * 86_400_000;

/**
 * El vendedor del cliente (D-149). Los links que comparte un vendedor llevan ?v=CODIGO: la tienda
 * lo recuerda 60 días en este navegador (el último link manda) y "Enviar a mi vendedor" le llega
 * directo a él.
 */
@Injectable({ providedIn: 'root' })
export class MiVendedorService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  readonly vendedor = signal<MiVendedor | null>(this.leer());

  /** Una vez al arrancar: mira el ?v= de cada navegación */
  iniciar(): void {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      const url = this.router.parseUrl(this.router.url);
      const codigo = String(url.queryParams['v'] ?? '').trim().toUpperCase();
      if (!/^[A-Z0-9]{6}$/.test(codigo)) return;
      // Se quita de la dirección para que no se comparta por accidente con el código de otro
      void this.router.navigate([], { queryParams: { v: null }, queryParamsHandling: 'merge', replaceUrl: true });
      if (this.vendedor()?.codigo === codigo) return this.guardar({ ...this.vendedor()!, en: Date.now() });
      this.buscar(codigo).subscribe({ next: (v) => v && this.guardar(v), error: () => undefined });
    });
  }

  buscar(codigo: string) {
    return this.http
      .get<ApiSuccess<{ codigo: string; nombre: string; whatsapp: string | null }>>(`${environment.apiUrl}/public/vendedores/${encodeURIComponent(codigo)}`)
      .pipe(map((r) => ({ ...r.data, en: Date.now() })));
  }

  guardar(v: MiVendedor): void {
    this.vendedor.set(v);
    try {
      localStorage.setItem(CLAVE, JSON.stringify(v));
    } catch {
      // Sin almacenamiento: vale para esta visita
    }
  }

  olvidar(): void {
    this.vendedor.set(null);
    try {
      localStorage.removeItem(CLAVE);
    } catch {
      // nada
    }
  }

  private leer(): MiVendedor | null {
    try {
      const v = JSON.parse(localStorage.getItem(CLAVE) ?? 'null') as MiVendedor | null;
      return v && Date.now() - v.en < VIGENCIA_MS ? v : null;
    } catch {
      return null;
    }
  }
}
